import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getEvents, getSiteCopy } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PAGE_SEO, breadcrumbLd } from "@/lib/seo";
import { track } from "@/lib/analytics";
import { Calendar, MapPin, Users, ArrowRight } from "lucide-react";
import { amp } from "@/lib/amp";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch (e) {
    return iso;
  }
}

export default function Events() {
  const { data: copy = {} } = useQuery({ queryKey: ["site-copy"], queryFn: getSiteCopy });
  const { data: events = [] } = useQuery({ queryKey: ["events"], queryFn: getEvents });

  return (
    <div className="exec-scope min-h-screen" data-testid="events-page">
      <Seo {...PAGE_SEO.events} jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Events", path: "/events" }])} />
      <section className="exec-container pt-24 md:pt-28 pb-10">
        <div className="max-w-[820px]">
          <p className="exec-eyebrow">Events</p>
          <h1 className="font-extralight text-[clamp(34px,4.8vw,60px)] leading-[1.1] mt-6">Rooms worth being in.</h1>
          <p data-testid="events-intro" className="exec-serif text-[clamp(18px,2vw,23px)] leading-[1.5] text-[color:var(--lead-ink)] mt-6">{copy.events_intro}</p>

          <div data-testid="events-how-strip" className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-[color:var(--rule)] pt-8">
            {[1, 2, 3].map((n) => (
              <div key={n}>
                <p className="exec-eyebrow">{copy[`events_how_${n}_label`]}</p>
                <p className="exec-serif text-[15.5px] leading-[1.5] text-[color:var(--lead-ink)] mt-2">{copy[`events_how_${n}_body`]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="exec-container pb-20">
        <div className="space-y-5">
          {events.map((e) => {
            const remaining = e.seats_remaining ?? 0;
            const registered = (e.seats_total || 0) - remaining;
            const toThreshold = Math.max((e.activation_threshold || 0) - registered, 0);
            return (
              <motion.div key={e.slug} {...fade} data-testid="event-card" className="exec-card p-8 sm:p-10">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="max-w-2xl">
                    <span className="exec-eyebrow">{amp(e.kind)}</span>
                    <h2 className="font-light text-[clamp(22px,2.6vw,30px)] leading-[1.18] mt-3">{e.title}</h2>
                    <p className="exec-serif text-[16.5px] leading-[1.55] text-[color:var(--lead-ink)] mt-3">{e.description}</p>

                    {(e.who_for || e.what_leave || e.why_now) && (
                      <dl data-testid="event-detail-lines" className="mt-4 space-y-2">
                        {e.who_for && (
                          <div className="exec-serif text-[15.5px] text-[color:var(--lead-ink)]">
                            <dt className="inline font-medium text-[color:var(--ink)]">Who this is for: </dt>
                            <dd className="inline">{e.who_for}</dd>
                          </div>
                        )}
                        {e.what_leave && (
                          <div className="exec-serif text-[15.5px] text-[color:var(--lead-ink)]">
                            <dt className="inline font-medium text-[color:var(--ink)]">What you will leave with: </dt>
                            <dd className="inline">{e.what_leave}</dd>
                          </div>
                        )}
                        {e.why_now && (
                          <div className="exec-serif text-[15.5px] text-[color:var(--lead-ink)]">
                            <dt className="inline font-medium text-[color:var(--ink)]">Why now: </dt>
                            <dd className="inline">{e.why_now}</dd>
                          </div>
                        )}
                      </dl>
                    )}

                    <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
                      <span className="flex items-center gap-2 text-[13px] tracking-[0.04em] text-[color:var(--lead-ink)]"><Calendar size={15} className="text-[color:var(--copper-deep)]" />{fmtDate(e.event_date)}</span>
                      <span className="flex items-center gap-2 text-[13px] tracking-[0.04em] text-[color:var(--lead-ink)]"><MapPin size={15} className="text-[color:var(--copper-deep)]" />{e.format}</span>
                      <span className="flex items-center gap-2 text-[13px] tracking-[0.04em] text-[color:var(--lead-ink)]"><Users size={15} className="text-[color:var(--copper-deep)]" />{remaining} of {e.seats_total} seats left</span>
                    </div>
                    {toThreshold > 0 && (
                      <p className="exec-serif text-[14px] text-[color:var(--grey)] mt-3">
                        Confirms when {e.activation_threshold} people register. We will email everyone the moment the room is set.{e.activation_date ? ` If it does not reach ${e.activation_threshold} by ${fmtDate(e.activation_date)}, your card is refunded automatically.` : " If it does not reach the threshold, your card is refunded automatically."}
                      </p>
                    )}
                  </div>
                  <div className="text-left lg:text-right shrink-0">
                    <p className="font-extralight text-[clamp(26px,3vw,36px)] leading-none">{e.price_label}</p>
                    <Link
                      to={`/events/${e.slug}`}
                      data-testid="event-register-button"
                      className="exec-btn exec-btn-primary mt-5"
                      onClick={() => track("door_click", { target: "event_register", event: e.slug })}
                    >
                      View and register <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
          {events.length === 0 && (
            <p className="exec-serif text-[17px] text-[color:var(--grey)]">No open events right now. Check back soon.</p>
          )}
        </div>
      </section>
    </div>
  );
}
